import React from 'react';
import { Link } from 'react-router-dom';

// Shared building blocks for the public marketing pages (/features and
// /onepager). Kept in one file so the two pages stay visually locked-in
// as a family — same nav, same footer, same integration strip, same
// screenshot frame treatment. Design palette mirrors calliq.snapcom.com
// so the two products read as siblings.

export const BRAND_BLUE = '#0066CC';
export const BRAND_NAVY = '#0A1F44';

// Integrations shown on the marketing pages. Deliberately trimmed to
// business-facing systems — infrastructure vendors (storage, push,
// specific AI providers) live under the hood and don't belong on the
// public capability list.
export const INTEGRATIONS = [
  { name: 'Bandwidth',        color: '#E4002B', category: 'Carrier' },
  { name: 'Salesforce',       color: '#00A1E0', category: 'CRM' },
  { name: 'Clio',             color: '#1E4C8C', category: 'Legal' },
  { name: 'Meevo',            color: '#F26522', category: 'Salon & Spa' },
  { name: 'ShipStation',      color: '#0072BC', category: 'E-commerce' },
  { name: 'Microsoft Teams',  color: '#4B53BC', category: 'Collaboration' },
  { name: 'AI',               color: '#8B5CF6', category: 'AI' },
  { name: 'Webhooks',         color: '#6B7280', category: 'Automation' },
];

export const MarketingNav = ({ variant = 'full' }) => (
  <nav className="w-full border-b border-slate-200 bg-white/95 backdrop-blur sticky top-0 z-30">
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
      <Link to="/features" className="flex items-center gap-2">
        <span className="w-8 h-8 rounded-lg flex items-center justify-center text-white font-bold text-sm" style={{ backgroundColor: BRAND_BLUE }}>S</span>
        <span className="text-lg font-semibold text-slate-900 tracking-tight">SnapText</span>
      </Link>
      <div className="flex items-center gap-3 sm:gap-5">
        {variant === 'full' ? (
          <Link to="/onepager" className="hidden sm:inline text-sm font-medium text-slate-600 hover:text-slate-900">One-pager</Link>
        ) : (
          <Link to="/features" className="hidden sm:inline text-sm font-medium text-slate-600 hover:text-slate-900">Full tour</Link>
        )}
        <a href="mailto:hello@snapcom.com?subject=SnapText%20-%20Request%20access&body=I'd%20like%20to%20learn%20more%20about%20SnapText."
           className="hidden sm:inline text-sm font-medium text-slate-600 hover:text-slate-900">
          Request access
        </a>
        <Link to="/login"
              className="text-sm font-semibold px-4 py-2 rounded-md text-white shadow-sm"
              style={{ backgroundColor: BRAND_BLUE }}>
          Sign in
        </Link>
      </div>
    </div>
  </nav>
);

export const MarketingFooter = () => (
  <footer className="border-t border-slate-200 bg-slate-50 mt-16">
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex flex-col sm:flex-row items-center justify-between gap-4">
      <div className="flex items-center gap-2 text-sm text-slate-500">
        <span className="w-6 h-6 rounded flex items-center justify-center text-white font-bold text-xs" style={{ backgroundColor: BRAND_BLUE }}>S</span>
        <span>SnapText — a Snapcom product</span>
      </div>
      <div className="flex items-center gap-5 text-sm text-slate-500">
        <Link to="/features" className="hover:text-slate-900">Full tour</Link>
        <Link to="/onepager" className="hover:text-slate-900">One-pager</Link>
        <Link to="/login" className="hover:text-slate-900">Sign in</Link>
        <a href="mailto:hello@snapcom.com" className="hover:text-slate-900">Contact</a>
      </div>
    </div>
  </footer>
);

export const IntegrationStrip = ({ label = 'Works with the tools you already use' }) => (
  <div className="py-8">
    {label && (
      <div className="text-center text-xs font-semibold tracking-wider text-slate-500 uppercase mb-5">
        {label}
      </div>
    )}
    <div className="flex flex-wrap items-center justify-center gap-2.5">
      {INTEGRATIONS.map(i => (
        <div key={i.name}
             className="flex items-center gap-2 px-3 py-1.5 rounded-md border border-slate-200 bg-white text-sm font-medium text-slate-700 shadow-sm">
          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: i.color }} />
          <span>{i.name}</span>
        </div>
      ))}
    </div>
  </div>
);

// A clean framed "screenshot" surface. Matches calliq treatment —
// rounded corners, subtle border, soft shadow, no browser chrome.
export const ScreenshotFrame = ({ children, caption }) => (
  <figure className="my-6">
    <div className="rounded-xl border border-slate-200 bg-white shadow-[0_20px_50px_-15px_rgba(15,23,42,0.25)] overflow-hidden">
      {children}
    </div>
    {caption && (
      <figcaption className="mt-3 text-center text-sm text-slate-500">{caption}</figcaption>
    )}
  </figure>
);

// Big-numeral metric callout — the calliq pages use these as a rhetorical
// device. Keep two or three per page to avoid diluting the punch.
export const MetricStat = ({ number, label, color = BRAND_BLUE }) => (
  <div className="text-center px-2">
    <div className="text-5xl sm:text-6xl font-extrabold tracking-tight" style={{ color }}>{number}</div>
    <div className="mt-2 text-sm text-slate-600 leading-snug">{label}</div>
  </div>
);

export const SectionHeader = ({ eyebrow, title, subtitle }) => (
  <div className="max-w-3xl mx-auto text-center mb-10">
    {eyebrow && (
      <div className="text-xs font-semibold tracking-wider uppercase mb-3" style={{ color: BRAND_BLUE }}>{eyebrow}</div>
    )}
    <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight" style={{ textWrap: 'balance' }}>{title}</h2>
    {subtitle && (
      <p className="mt-4 text-lg text-slate-600 leading-relaxed" style={{ textWrap: 'balance' }}>{subtitle}</p>
    )}
  </div>
);

export const PrimaryCTA = ({ href = 'mailto:hello@snapcom.com?subject=SnapText%20-%20Request%20access', children }) => (
  <a href={href}
     className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md text-white font-semibold shadow-sm hover:opacity-95"
     style={{ backgroundColor: BRAND_BLUE }}>
    {children}
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
    </svg>
  </a>
);

export const SecondaryCTA = ({ to, href, children }) => {
  const cls = "inline-flex items-center gap-2 px-5 py-2.5 rounded-md border font-semibold hover:bg-slate-50";
  const style = { color: BRAND_BLUE, borderColor: BRAND_BLUE };
  if (href) return <a href={href} className={cls} style={style}>{children}</a>;
  return <Link to={to} className={cls} style={style}>{children}</Link>;
};

// -----------------------------------------------------------------
// Mockups — small React renderings that stand in for real screenshots.
// Kept intentionally sparse (no live data, no interactivity) so they
// read as illustrative previews of the product surface, not the real
// app embedded in a marketing page.
// -----------------------------------------------------------------

const MockChip = ({ children, color = BRAND_BLUE }) => (
  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold"
        style={{ backgroundColor: color + '1A', color }}>{children}</span>
);

export const InboxMockup = () => (
  <div className="grid grid-cols-12 min-h-[360px] bg-slate-50">
    {/* Left column: conversation list */}
    <div className="col-span-4 border-r border-slate-200 bg-white">
      <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
        <span className="text-sm font-semibold text-slate-800">Inbox</span>
        <MockChip color="#EF4444">3 new</MockChip>
      </div>
      {[
        { name: 'Jordan P.', msg: 'Can we push our appt to 3?', unread: true, ago: '2m' },
        { name: 'Maria S.',  msg: 'Yes that works, thanks!',    unread: false, ago: '9m' },
        { name: 'Nikhil V.', msg: 'Received, appreciate it.',    unread: false, ago: '32m' },
        { name: 'Tara Q.',   msg: 'Order confirmed 🎉',          unread: true, ago: '1h' },
        { name: 'Devin K.',  msg: 'Sounds good — Friday works.', unread: false, ago: '3h' },
      ].map((c, i) => (
        <div key={i} className={`px-4 py-3 border-b border-slate-100 ${c.unread ? 'bg-blue-50/50' : ''}`}>
          <div className="flex items-center justify-between">
            <span className={`text-sm ${c.unread ? 'font-bold text-slate-900' : 'font-medium text-slate-700'}`}>{c.name}</span>
            <span className="text-[10px] text-slate-400">{c.ago}</span>
          </div>
          <div className="text-xs text-slate-500 truncate mt-0.5">{c.msg}</div>
        </div>
      ))}
    </div>
    {/* Right column: conversation thread */}
    <div className="col-span-8 flex flex-col">
      <div className="px-5 py-3 border-b border-slate-200 bg-white flex items-center justify-between">
        <div>
          <div className="text-sm font-semibold text-slate-800">Jordan P.</div>
          <div className="text-[11px] text-slate-500 font-mono">(555) 214-9098</div>
        </div>
        <div className="flex items-center gap-2">
          <MockChip color="#10B981">Available</MockChip>
          <MockChip color={BRAND_BLUE}>Assigned to you</MockChip>
        </div>
      </div>
      <div className="flex-1 px-5 py-4 space-y-3">
        <div className="flex justify-start">
          <div className="max-w-[75%] px-3 py-2 rounded-2xl rounded-tl-sm bg-white border border-slate-200 text-sm text-slate-800 shadow-sm">
            Hi! Just confirming my 2pm on Thursday.
          </div>
        </div>
        <div className="flex justify-end">
          <div className="max-w-[75%] px-3 py-2 rounded-2xl rounded-tr-sm text-sm text-white shadow-sm" style={{ backgroundColor: BRAND_BLUE }}>
            Confirmed for 2pm Thursday — see you then!
          </div>
        </div>
        <div className="flex justify-start">
          <div className="max-w-[75%] px-3 py-2 rounded-2xl rounded-tl-sm bg-white border border-slate-200 text-sm text-slate-800 shadow-sm">
            Can we push our appt to 3?
          </div>
        </div>
      </div>
      <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 flex items-center gap-2">
        <div className="flex-1 h-9 rounded-md bg-white border border-slate-300 flex items-center px-3 text-sm text-slate-400">Type your reply…</div>
        <button className="px-3 h-9 rounded-md text-white text-sm font-semibold" style={{ backgroundColor: BRAND_BLUE }}>Send</button>
      </div>
    </div>
  </div>
);

export const RoutingMockup = () => (
  <div className="p-6 bg-slate-50 min-h-[320px]">
    <div className="max-w-md mx-auto space-y-3">
      {[
        { icon: '⚡', title: 'Basics', summary: 'Round Robin · 6 users', open: false },
        { icon: '📨', title: 'When Messages Arrive', summary: 'Auto-reply, Greeting, Auto-tag', open: true },
        { icon: '🔀', title: 'Routing Rules', summary: 'Sticky, VIP, 2 keywords', open: false },
        { icon: '🛡️', title: 'Fallbacks', summary: 'Overflow → queue', open: false },
      ].map((s, i) => (
        <div key={i} className="rounded-lg border border-slate-200 overflow-hidden bg-white">
          <div className={`flex items-center justify-between px-4 py-2.5 text-sm ${s.open ? 'text-white' : 'bg-slate-700 text-white'}`}
               style={s.open ? { backgroundColor: BRAND_BLUE } : undefined}>
            <div className="flex items-center gap-2">
              <span>{s.icon}</span>
              <span className="font-semibold">{s.title}</span>
            </div>
            <span className="text-[10px] text-white/70">{s.summary}</span>
          </div>
          {s.open && (
            <div className="p-3 space-y-2 text-xs">
              <div className="flex items-center justify-between px-3 py-2 border border-slate-200 rounded-md">
                <span className="font-medium text-slate-700">💬 Auto-Reply & Business Hours</span>
                <span className="text-emerald-600 font-semibold">ON</span>
              </div>
              <div className="flex items-center justify-between px-3 py-2 border border-slate-200 rounded-md">
                <span className="font-medium text-slate-700">👋 New Contact Greeting</span>
                <span className="text-emerald-600 font-semibold">ON</span>
              </div>
              <div className="flex items-center justify-between px-3 py-2 border border-slate-200 rounded-md">
                <span className="font-medium text-slate-700">🏷️ Auto-Tag Conversations</span>
                <span className="text-slate-400 font-semibold">OFF</span>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  </div>
);

export const BlastMockup = () => (
  <div className="p-6 bg-slate-50 min-h-[320px]">
    <div className="max-w-lg mx-auto bg-white rounded-lg border border-slate-200 p-5 space-y-4 shadow-sm">
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold text-slate-800">New text blast</span>
        <MockChip>Draft</MockChip>
      </div>
      <div>
        <div className="text-[11px] font-semibold text-slate-500 mb-1">Recipient list</div>
        <div className="px-3 py-2 border border-slate-200 rounded-md text-sm text-slate-700 flex items-center justify-between">
          <span>VIP Customers · <span className="text-slate-400">412 contacts</span></span>
          <span className="text-xs text-slate-400">Change</span>
        </div>
      </div>
      <div>
        <div className="text-[11px] font-semibold text-slate-500 mb-1">Message</div>
        <div className="px-3 py-2 border border-slate-200 rounded-md min-h-[72px] text-sm text-slate-700">
          Hi {'{first_name}'} — thanks for being a VIP. Your exclusive offer is inside: snpc.co/vip. Reply STOP to opt out.
        </div>
        <div className="mt-1 flex items-center justify-between text-[11px] text-slate-500">
          <span>1 segment · 143 chars</span>
          <span>Personalizes per contact</span>
        </div>
      </div>
      <div>
        <div className="text-[11px] font-semibold text-slate-500 mb-1">Schedule</div>
        <div className="grid grid-cols-2 gap-2">
          <div className="px-3 py-2 border-2 rounded-md text-sm font-semibold text-center" style={{ borderColor: BRAND_BLUE, color: BRAND_BLUE }}>Send now</div>
          <div className="px-3 py-2 border border-slate-200 rounded-md text-sm text-slate-500 text-center">Later…</div>
        </div>
      </div>
      <button className="w-full py-2.5 rounded-md text-white font-semibold text-sm" style={{ backgroundColor: BRAND_BLUE }}>Review &amp; send to 412 recipients</button>
    </div>
  </div>
);

export const AnalyticsMockup = () => (
  <div className="p-6 bg-slate-50 min-h-[320px] grid grid-cols-3 gap-3">
    <div className="col-span-1 bg-white border border-slate-200 rounded-lg p-4">
      <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Messages / week</div>
      <div className="text-3xl font-extrabold text-slate-900 mt-1">12,480</div>
      <div className="text-[11px] font-semibold mt-1" style={{ color: BRAND_BLUE }}>▲ 8.2% vs last</div>
    </div>
    <div className="col-span-1 bg-white border border-slate-200 rounded-lg p-4">
      <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Avg response</div>
      <div className="text-3xl font-extrabold text-slate-900 mt-1">1m 42s</div>
      <div className="text-[11px] font-semibold text-emerald-600 mt-1">▼ 24s faster</div>
    </div>
    <div className="col-span-1 bg-white border border-slate-200 rounded-lg p-4">
      <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Opt-ins</div>
      <div className="text-3xl font-extrabold text-slate-900 mt-1">96.3%</div>
      <div className="text-[11px] font-semibold text-emerald-600 mt-1">Above target</div>
    </div>
    <div className="col-span-3 bg-white border border-slate-200 rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm font-semibold text-slate-800">Volume by day</span>
        <MockChip>Last 14 days</MockChip>
      </div>
      <div className="flex items-end gap-1.5 h-24">
        {[38, 52, 61, 44, 70, 82, 58, 66, 74, 88, 72, 91, 80, 95].map((h, i) => (
          <div key={i} className="flex-1 rounded-t" style={{ height: `${h}%`, backgroundColor: i === 13 ? BRAND_BLUE : BRAND_BLUE + '55' }} />
        ))}
      </div>
    </div>
  </div>
);

export const MobileMockup = () => (
  <div className="p-8 bg-slate-50 min-h-[320px] flex items-center justify-center">
    <div className="w-56 rounded-[2rem] border-[8px] border-slate-900 overflow-hidden shadow-xl bg-white">
      <div className="h-6 bg-slate-900" />
      <div className="p-3 space-y-2">
        <div className="px-2 py-1.5 rounded-lg bg-blue-50 flex items-center justify-between">
          <div>
            <div className="text-[10px] font-bold text-slate-800">Alex M.</div>
            <div className="text-[9px] text-slate-500 truncate">Just landed — heading over</div>
          </div>
          <MockChip color="#EF4444">1</MockChip>
        </div>
        <div className="px-2 py-1.5 rounded-lg bg-white border border-slate-100 flex items-center justify-between">
          <div>
            <div className="text-[10px] font-medium text-slate-700">Kim R.</div>
            <div className="text-[9px] text-slate-500 truncate">Thanks!</div>
          </div>
          <span className="text-[9px] text-slate-400">2h</span>
        </div>
        <div className="px-2 py-1.5 rounded-lg bg-white border border-slate-100 flex items-center justify-between">
          <div>
            <div className="text-[10px] font-medium text-slate-700">Sam L.</div>
            <div className="text-[9px] text-slate-500 truncate">See you Fri</div>
          </div>
          <span className="text-[9px] text-slate-400">4h</span>
        </div>
      </div>
    </div>
  </div>
);
